import { useState, useEffect } from "react";
import Loader from "components/Loader";
import ResultTableSingleKeyWords from "components/Tables/ResultTableSingleKeyWords";
import { submitKeywordAnalysisRequest } from "services/buddyXApiHubService";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";
import ToastAlert from "components/Alert/ToastAlert";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [webSite, setWebSite] = useState("");
  const [formData, setFormData] = useState({
    keywords: [],
    domainLink: "",
  });
  const [showSearchItem, setShowSearchItem] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const firstLevelTopic = "SEO";
  const secoundLevelTopic = "Ranking";
  const thirdLevelTopic = "Single Keyword";

  const [errors, setErrors] = useState({ keyword: "", webSite: "" });
  const [data, setData] = useState([]);

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (
      name === "keyword" &&
      !Validator.validateRequired(keyword, Validator.validateSingleKeyword)
    ) {
      error = "Please enter keywords";
    } else if (
      name === "webSite" &&
      !Validator.validateOptional(value, Validator.validateUrl)
    ) {
      error = "Invalid URL";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setShowSearchItem("");
    setData([]);
    setIsResultGot(false);
    setKeyword("");
    setWebSite("");
    setShowErrorNotification(false);
    setErrorMessage("");
    setErrors({ keyword: "", webSite: "" });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await submitKeywordAnalysisRequest(formData,"single_keyword_ranking", handleInsufficientCredits);
      setShowSearchItem(formData.keywords[0]);
      setData(result);
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(error.message || "Failed to analyze keyword rankings. Please try again.");
      setShowErrorNotification(true);
      setIsResultGot(false);
    }
    setIsLoading(false);
  };

  const handleWebSiteChange = (e) => {
    setWebSite(e.target.value);
  };

  const handleInsufficientCredits = (balanceData) => {
    
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  useEffect(() => {
    let isValid =
      Validator.validateRequired(keyword, Validator.validateSingleKeyword) &&
      Validator.validateOptional(webSite, Validator.validateUrl);
    if (isValid) {
      setFormData({
        domainLink: webSite === "" ? null : webSite,
        keywords: [keyword],
      });
    }
    setIsFormValid(isValid);
  }, [keyword, webSite]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secoundLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
      Tracking keyword rankings for a given keyword provides essential insights into a page's or site's competitive positioning in search engine results pages (SERPs), enabling data-driven adjustments to maintain or elevate visibility amid algorithm fluctuations. In the 2025 search environment, characterized by AI-enhanced personalization and real-time query processing, consistent monitoring of this metric can correlate with 20-35% gains in organic traffic by revealing opportunities for content refinement or link-building, while mitigating risks from volatile rankings that could otherwise erode market share and user acquisition efficiency.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "Single Keyword Ranking helps you track how well your website ranks for specific keywords in search results. Monitor your search engine performance and optimize your content strategy based on ranking data."
            }
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">
          Find Your Rankings
        </div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter a keyword and optionally a website URL to check rankings.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="keyword"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={handleKeywordChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.keyword}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type4')}
                  />
                </Col>
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="webSite"
                    placeholder="Enter website URL (optional)"
                    value={webSite}
                    onChange={handleWebSiteChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.webSite}
                    className="px-2"
                    style={getInputFieldStyle('type4')}
                  />
                </Col>
                <div className="d-flex justify-content-start align-items-end">
                  <Button
                    variant="dark"
                    className="me-1"
                    onClick={handleSubmit}
                    disabled={!isFormValid || isLoading}
                    style={getButtonStyle('type4', 'primary')}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" />
                        Generating...
                      </>
                    ) : (
                      "Search"
                    )}
                  </Button>
                  <Button
                    variant="outline-dark"
                    className="me-1"
                    onClick={handleReset}
                    style={getButtonStyle('type4', 'outline')}
                  >
                    Reset
                  </Button>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div>
            <p className="d-flex fw-bold mb-0">
              Rankings for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <ResultTableSingleKeyWords data={data} />}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
